/**
 * useIntegrationGridConfig
 *
 * Builds the `DataGridConfig` for the IntegrationListing grid.
 * All data-fetching is delegated to `useIntegrationListing`; this hook
 * only concerns itself with how the data is presented.
 *
 * Column definitions follow the task spec:
 *   Provider | Module | Integration Type | Account ID | Integration Label
 *   Region | Status | Last Updated | Actions
 */

import { DataGridConfig } from 'components/DataGrid';
import JsonToTable from 'components/JsonToTable';
import { useAtom, useAtomValue } from 'jotai';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { Popover, PopoverBody, PopoverHeader } from 'reactstrap';
import AgingOutLastDate from 'views/Admin/Dashboard/components/AgingOutLastDate/AgingOutLastDate';

import { navigateToBPAFindings } from './navigation';
import IntegrationListingRowActions from './RowActions';
import '../../styles.css';
import { useIntegrationListing } from '../../hooks/useIntegrationListing';
import {
  activeColumnKeysAtom,
  allColumnKeysAtom,
  DEFAULT_ACTIVE_INTEGRATION_COLUMN_KEYS,
  DEFAULT_ALL_INTEGRATION_COLUMN_KEYS,
  DEFAULT_DELETED_INTEGRATION_COLUMN_KEYS,
  deletedColumnKeysAtom,
  integrationDataAtom,
  orderByAtom,
  paginationConfigAtom,
  parsedFilterValuesAtom,
  selectedRowIdsAtom,
  sortOrderAtom,
} from '../../state';

interface ActiveScanSource {
  benchmark?: string | null;
  region?: string | null;
  scan_started_at?: string | null;
  started_at?: string | null;
  startedAt?: string | null;
  created_at?: string | null;
  createdAt?: string | null;
}

const getActiveScansData = (
  activeScans: ActiveScanSource[] | undefined,
  limit?: number,
) => {
  const scans = Array.isArray(activeScans) ? activeScans : [];
  const scansToFormat = limit === undefined ? scans : scans.slice(0, limit);

  return scansToFormat.map((scan) => ({
    benchmark: scan?.benchmark || '',
    region: scan?.region || '',
    started_at:
      scan?.scan_started_at ||
      scan?.started_at ||
      scan?.startedAt ||
      scan?.created_at ||
      scan?.createdAt ||
      '',
  }));
};

interface ActiveScansCountProps {
  integrationId: number;
  integrationLabel: string;
  activeScanCount: number;
  activeScans: ActiveScanSource[] | undefined;
}

const ActiveScansCount: React.FC<ActiveScansCountProps> = ({
  integrationId,
  integrationLabel,
  activeScanCount,
  activeScans,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const scanData = useMemo(
    () => (isPopoverOpen ? getActiveScansData(activeScans) : []),
    [activeScans, isPopoverOpen],
  );
  const targetId = `bpa-active-scans-${integrationId}`;
  const closePopover = () => setIsPopoverOpen(false);

  useEffect(() => {
    if (!isPopoverOpen) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsPopoverOpen(false);
        document.getElementById(targetId)?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isPopoverOpen, targetId]);

  const countButton = (
    <button
      id={targetId}
      type="button"
      className="bpa-active-scans-count"
      aria-label={`View ${activeScanCount} active scans for ${integrationLabel}`}
      aria-expanded={isPopoverOpen}
      aria-haspopup="dialog"
      aria-controls={
        isPopoverOpen ? `bpa-active-scans-details-${integrationId}` : undefined
      }
      onClick={() => ReactTooltip.hide()}
    >
      {activeScanCount}
    </button>
  );

  return (
    <>
      {countButton}

      <Popover
        isOpen={isPopoverOpen}
        target={targetId}
        toggle={() => setIsPopoverOpen((isOpen) => !isOpen)}
        trigger="legacy"
        placement="bottom"
        boundariesElement="viewport"
        fade
        className="bpa-active-scans-popover"
      >
        <div
          id={`bpa-active-scans-details-${integrationId}`}
          role="dialog"
          aria-label={`Active scans for ${integrationLabel}`}
        >
          <PopoverHeader className="bpa-active-scans-popover-header">
            <span>Active Scans ({activeScanCount})</span>
            <button
              type="button"
              className="close bpa-active-scans-popover-close"
              aria-label="Close active scans"
              onClick={closePopover}
            >
              <span aria-hidden="true">×</span>
            </button>
          </PopoverHeader>
          <PopoverBody>
            <div className="bpa-active-scans-integration-name">
              {integrationLabel}
            </div>
            {scanData.length > 0 ? (
              <JsonToTable
                data={scanData}
                cssClasses="table table-sm table-hover mb-0 bpa-active-scans-table"
              />
            ) : (
              <div className="p-3 text-muted">
                Active scan details are not available.
              </div>
            )}
          </PopoverBody>
        </div>
      </Popover>
    </>
  );
};

export function useIntegrationGridConfig() {
  const { data, loading } = useIntegrationListing();

  const listingData = useAtomValue(integrationDataAtom);
  const filterValues = useAtomValue(parsedFilterValuesAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);
  const [activeColumnKeys, setActiveColumnKeys] = useAtom(activeColumnKeysAtom);
  const [deletedColumnKeys, setDeletedColumnKeys] = useAtom(
    deletedColumnKeysAtom,
  );
  const [allColumnKeys, setAllColumnKeys] = useAtom(allColumnKeysAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const [orderBy, setOrderBy] = useAtom(orderByAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom);

  const statusValues = filterValues.status;
  const selectedStatus = Array.isArray(statusValues)
    ? statusValues[0] || ''
    : statusValues || '';
  const isDeletedFilterSelected = selectedStatus === 'deleted';
  const isAllFilterSelected = selectedStatus === '';

  const columnsDisplayConfig = useMemo(
    () =>
      isDeletedFilterSelected
        ? {
            enabledColumnKeys: deletedColumnKeys,
            defaultColumnKeys: DEFAULT_DELETED_INTEGRATION_COLUMN_KEYS,
            keysToBeExcluded: [
              'status',
              'active_scan_count',
              'active_finding_count',
              'last_sync_at',
            ],
            onColumnsDisplayChange: setDeletedColumnKeys,
          }
        : isAllFilterSelected
          ? {
              enabledColumnKeys: allColumnKeys,
              defaultColumnKeys: DEFAULT_ALL_INTEGRATION_COLUMN_KEYS,
              onColumnsDisplayChange: setAllColumnKeys,
            }
          : {
              enabledColumnKeys: activeColumnKeys,
              defaultColumnKeys: DEFAULT_ACTIVE_INTEGRATION_COLUMN_KEYS,
              keysToBeExcluded: [
                'status',
                'open_vuln_counts_at_deletion',
                'closed_vuln_counts_at_deletion',
                'delete_at',
                'delete_by',
              ],
              onColumnsDisplayChange: setActiveColumnKeys,
            },
    [
      activeColumnKeys,
      allColumnKeys,
      deletedColumnKeys,
      isAllFilterSelected,
      isDeletedFilterSelected,
      setActiveColumnKeys,
      setAllColumnKeys,
      setDeletedColumnKeys,
    ],
  );

  const config: DataGridConfig = useMemo(
    () => ({
      rowIdKey: 'id',

      // ── Column definitions ────────────────────────────────────────────────
      columns: [
        {
          key: 'provider_code',
          label: 'Provider',
          sortable: true,
          render: (row) => row.provider_code || '—',
        },
        {
          key: 'module_code',
          label: 'Module',
          sortable: true,
          render: (row) => row.module_code || '—',
        },
        {
          key: 'integration_type',
          label: 'Integration Level',
          sortable: true,
          render: (row) =>
            row.integration_type
              ? row.integration_type
                  .replace(/_/g, ' ')
                  .toLowerCase()
                  .replace(/\b\w/g, (c) => c.toUpperCase())
              : '—',
        },
        {
          key: 'account_id',
          label: 'Account ID',
          sortable: true,
          enableCopyToClipboard: true,
        },
        {
          key: 'integration_label',
          label: 'Integration Name',
          sortable: true,
          maxCharLength: 30,
        },
        {
          key: 'region',
          label: 'Region',
          sortable: true,
          render: (row) => row.region || '—',
        },
        {
          key: 'status',
          label: 'Status',
          sortable: false,
          render: (row) =>
            row.is_deleted ? 'Deleted' : row.is_active ? 'Active' : 'Inactive',
        },
        {
          key: 't12_integration_id',
          label: 'Integration ID',
          sortable: true,
          enableCopyToClipboard: true,
        },
        {
          key: 'created_at',
          label: 'Created At',
          sortable: true,
          render: (row) =>
            row.created_at
              ? moment(row.created_at).format('MMM DD, YYYY h:mm A')
              : '—',
        },
        {
          key: 'updated_at',
          label: 'Updated At',
          sortable: true,
          render: (row) =>
            row.updated_at
              ? moment(row.updated_at).format('MMM DD, YYYY h:mm A')
              : '—',
        },
        {
          key: 'last_sync_at',
          label: 'Last Synced At',
          sortable: true,
          render: (row) =>
            row.is_deleted ? (
              '—'
            ) : (
              <AgingOutLastDate
                lastDate={row.last_sync_at || row.updated_at}
                totalDays={1 * 30}
                dangerDaysDiff={10}
                warningDaysDiff={20}
                tooltipTitle="Integration is inactive and will age out"
              />
            ),
        },
        {
          key: 'active_scan_count',
          label: 'Active Scans',
          sortable: true,
          headerTooltip:
            'Hover to preview active scans or click the count to view all',
          overrideContents: (row) => {
            if (row.is_deleted) return '—';
            const count = row.active_scan_count ?? 0;
            return count > 0 ? (
              <ActiveScansCount
                integrationId={row.id}
                integrationLabel={row.integration_label}
                activeScanCount={count}
                activeScans={row.active_scans}
              />
            ) : (
              count
            );
          },
          rowCellStyle: { textAlign: 'center' },
          tooltip: (row) => {
            if (row.is_deleted) {
              return 'Active scans are not retained for deleted integrations';
            }

            const count = row.active_scan_count ?? 0;
            if (count === 0) return 'No active scans found';

            return (
              <>
                Below is the list of active scans for this integration <br />
                <JsonToTable data={getActiveScansData(row.active_scans, 5)} />
                <div className="px-2 pb-2 font-weight-bold">
                  {count > 5
                    ? `Showing the first 5 scans. Click the count to view all ${count} active scans.`
                    : 'Click the count to view all active scans.'}
                </div>
              </>
            );
          },
        },
        {
          key: 'active_finding_count',
          label: 'Findings',
          sortable: true,
          headerTooltip: 'Total number of active findings for this integration',
          headerCellStyle: { textAlign: 'center' },
          rowCellStyle: { textAlign: 'center' },
          overrideContents: (row) => {
            if (row.is_deleted) return '—';
            const count = row.active_finding_count ?? 0;
            return count > 0 ? (
              <label
                className="label btn slim"
                style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
                onClick={() =>
                  navigateToBPAFindings(row.id, row.integration_label)
                }
              >
                {count}
              </label>
            ) : (
              <label
                className="label btn slim"
                style={{ color: 'rgb(255, 255, 255)', cursor: 'default' }}
              >
                {count}
              </label>
            );
          },
        },
        {
          key: 'open_vuln_counts_at_deletion',
          label: 'Open Findings at Deletion',
          sortable: true,
          headerTooltip:
            'Number of active, non-ignored findings when the integration was deleted',
          overrideContents: (row) =>
            row.is_deleted ? (
              <label
                className="label btn slim"
                style={{ color: 'rgb(255, 255, 255)', cursor: 'default' }}
              >
                {row.open_vuln_counts_at_deletion ?? 0}
              </label>
            ) : (
              '—'
            ),
          headerCellStyle: { textAlign: 'center' },
          rowCellStyle: { textAlign: 'center' },
        },
        {
          key: 'closed_vuln_counts_at_deletion',
          label: 'Closed Findings at Deletion',
          sortable: true,
          headerTooltip:
            'Number of fixed or ignored findings when the integration was deleted',
          overrideContents: (row) =>
            row.is_deleted ? (
              <label
                className="label btn slim"
                style={{ color: 'rgb(255, 255, 255)', cursor: 'default' }}
              >
                {row.closed_vuln_counts_at_deletion ?? 0}
              </label>
            ) : (
              '—'
            ),
          headerCellStyle: { textAlign: 'center' },
          rowCellStyle: { textAlign: 'center' },
        },
        {
          key: 'delete_at',
          label: 'Deleted At',
          sortable: true,
          render: (row) =>
            row.delete_at
              ? moment(row.delete_at).format('MMM DD, YYYY h:mm A')
              : '—',
        },
        {
          key: 'delete_by',
          label: 'Deleted By',
          sortable: true,
          render: (row) => row.delete_by || '—',
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
          render: (row) => <IntegrationListingRowActions row={row} />,
          headerCellStyle: {
            textAlign: 'right',
            maxWidth: 'auto',
            minWidth: '150px !important',
          },
          rowCellStyle: {
            textAlign: 'right',
            minWidth: '150px !important',
            maxWidth: 'auto',
            whiteSpace: 'nowrap',
          },
        },
      ],

      // ── Pagination ────────────────────────────────────────────────────────
      paginationConfig: {
        total_count: listingData.count,
        // DataGrid uses 0-based page numbers internally
        pageNumber: paginationConfig.page - 1,
        pageSize: paginationConfig.pageSize,
        onPageChange: (newPage) =>
          setPaginationConfig({ ...paginationConfig, page: newPage + 1 }),
        onPageSizeChange: (pageSize) =>
          setPaginationConfig({ page: 1, pageSize }),
      },

      // ── Sorting ───────────────────────────────────────────────────────────
      sortingConfig: {
        orderBy: orderBy,
        order: sortOrder,
        onOrderByChange: (column) => setOrderBy(column),
        onOrderChange: (order) => setSortOrder(order as 'asc' | 'desc'),
      },

      // ── Row selection ─────────────────────────────────────────────────────
      rowSelectionConfig: isDeletedFilterSelected
        ? undefined
        : {
            preSelectedRows: selectedRowIds,
            onSelectionChange: setSelectedRowIds,
          },

      // ── Row Styling ───────────────────────────────────────────────────────
      rowConfig: {
        classRules: [
          {
            className: 'applied-row',
            ruleFunction: (rowData) => rowData.isAppliedForDashboard === true,
          },
        ],
      },

      // ── Column visibility ─────────────────────────────────────────────────
      columnsDisplayConfig,
    }),
    [
      listingData.count,
      paginationConfig,
      setPaginationConfig,
      orderBy,
      setOrderBy,
      sortOrder,
      setSortOrder,
      selectedRowIds,
      setSelectedRowIds,
      isDeletedFilterSelected,
      columnsDisplayConfig,
    ],
  );

  return { config, data, loading };
}
